import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * Карточка клиники: то, чем клиника подписывается под документом.
 *
 * Заведена ради печати и только ради неё. План лечения, договор, счёт и
 * справка обязаны нести полное наименование, ИНН, ОГРН, адрес и номер
 * лицензии — без них бумага не документ, а распечатка экрана. Держать эти поля
 * в `platform.tenant` было бы соблазнительно, но `tenant` — учётная запись
 * арендатора платформы; реквизиты же принадлежат клинике как юридическому лицу
 * и меняются по своим поводам: переезд, перелицензирование, смена директора.
 *
 * ИНН, ОГРН и юридический адрес не обязательны для заведения карточки: их не
 * всегда знают наизусть в первый день, а требовать полный устав ради названия
 * и телефона значило бы отложить работу с картой ради бумаги, которую ещё
 * ищут в папке. Печать документов при этом не разрешается, пока их нет, —
 * `missingForPrint` называет недостающее по имени, тем же способом, что и для
 * лицензии.
 *
 * Одна запись на арендатора. Сети клиник с разными юрлицами в фазе 1 нет; она
 * появится вместе с Odonix Cloud, и тогда карточка станет карточкой филиала —
 * поэтому ключ здесь `tenantId`, а не отдельный идентификатор записи.
 *
 * Здесь проверяется форма реквизита, а контрольные суммы — в
 * `packages/core/src/requisites`: схема отвечает на вопрос «это вообще ИНН»,
 * ядро — «existует ли такой ИНН». Обратный порядок сделал бы контракты
 * зависимыми от ядра, которое само зависит от контрактов.
 *
 * Документация модуля: docs/03-modules/19-clinic-profile.md
 */

/** Юрлицо — 10 цифр, предприниматель — 12. Частный кабинет чаще второе. */
const inn = z.string().trim().regex(/^(\d{10}|\d{12})$/, 'ИНН — 10 или 12 цифр');

/** Пятый и шестой знаки бывают латинскими буквами у крупных плательщиков. */
const kpp = z.string().trim().regex(/^\d{4}[\dA-Z]{2}\d{3}$/, 'КПП — девять знаков');

const ogrn = z.string().trim().regex(/^(\d{13}|\d{15})$/, 'ОГРН — 13 цифр, ОГРНИП — 15');

/**
 * Номер лицензии на медицинскую деятельность. Единого формата нет: в реестре
 * живут и «ЛО-77-01-012345», и «Л041-01137-77/00368226», поэтому проверяется
 * длина и наличие цифр, а не шаблон.
 */
const license = z.string().trim().min(5).max(50);

/** Телефон в документе печатается как введён: клиника знает свой формат лучше. */
const phone = z.string().trim().min(5).max(30);

export const ClinicProfile = z.object({
  tenantId: Id,
  /** «Общество с ограниченной ответственностью „Одоникс“» — как в ЕГРЮЛ. */
  legalName: z.string().min(3).max(300),
  /** Как клинику зовут пациенты. Печатается в шапке крупно. */
  brandName: z.string().min(2).max(200),
  /** Пусто у клиники, которая ещё не внесла ИНН, — печать документов запрещена, см. `missingForPrint`. */
  inn: inn.nullable(),
  /** У предпринимателя КПП нет — это законно, а не незаполненное поле. */
  kpp: kpp.nullable(),
  /** Пусто по той же причине, что и ИНН: вносят, когда под рукой выписка. */
  ogrn: ogrn.nullable(),
  /** Юридический адрес: он же адрес в договоре. Пусто — как ИНН и ОГРН. */
  legalAddress: z.string().min(5).max(500).nullable(),
  /** Адрес приёма, если он другой. Пациент идёт по нему, а не по юридическому. */
  actualAddress: z.string().min(5).max(500).nullable(),
  /**
   * Пусто у клиники, которая ещё не внесла лицензию. Печать документов в этом
   * состоянии запрещена — см. `missingForPrint`.
   */
  licenseNumber: license.nullable(),
  /** Дата выдачи лицензии, `YYYY-MM-DD`. */
  licenseIssuedAt: z.iso.date().nullable(),
  /** Кто выдал: «Департамент здравоохранения города Москвы». */
  licenseIssuer: z.string().max(300).nullable(),
  phone,
  email: z.email().nullable(),
  website: z.url().nullable(),
  /**
   * Кто подписывает документы от имени клиники: ФИО и должность отдельно —
   * в бланке они стоят в разных местах.
   */
  directorName: z.string().min(3).max(200).nullable(),
  directorTitle: z.string().max(100).nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type ClinicProfile = z.infer<typeof ClinicProfile>;

/**
 * Заведение карточки.
 *
 * Обязательное здесь — то немногое, что клиника точно знает в день
 * регистрации: как она называется и по какому телефону её искать. ИНН, ОГРН,
 * юридический адрес, лицензия и подписант необязательны: их приносят позже, а
 * до тех пор система работает, просто не печатает документов.
 */
export const CreateClinicProfileRequest = z.object({
  legalName: z.string().min(3).max(300),
  brandName: z.string().min(2).max(200),
  inn: inn.optional(),
  kpp: kpp.optional(),
  ogrn: ogrn.optional(),
  legalAddress: z.string().min(5).max(500).optional(),
  actualAddress: z.string().min(5).max(500).optional(),
  licenseNumber: license.optional(),
  licenseIssuedAt: z.iso.date().optional(),
  licenseIssuer: z.string().max(300).optional(),
  phone,
  email: z.email().optional(),
  website: z.url().optional(),
  directorName: z.string().min(3).max(200).optional(),
  directorTitle: z.string().max(100).optional(),
});
export type CreateClinicProfileRequest = z.infer<typeof CreateClinicProfileRequest>;

/**
 * Правка карточки. Все поля необязательны: реквизиты меняют по одному —
 * переехали, сменили директора, перевыпустили лицензию, — и требовать полный
 * набор ради смены телефона значило бы заставлять перепечатывать устав.
 */
export const UpdateClinicProfileRequest = z.object({
  legalName: z.string().min(3).max(300).optional(),
  brandName: z.string().min(2).max(200).optional(),
  inn: inn.nullable().optional(),
  kpp: kpp.nullable().optional(),
  ogrn: ogrn.nullable().optional(),
  legalAddress: z.string().min(5).max(500).nullable().optional(),
  actualAddress: z.string().min(5).max(500).nullable().optional(),
  licenseNumber: license.nullable().optional(),
  licenseIssuedAt: z.iso.date().nullable().optional(),
  licenseIssuer: z.string().max(300).nullable().optional(),
  phone: phone.optional(),
  email: z.email().nullable().optional(),
  website: z.url().nullable().optional(),
  directorName: z.string().min(3).max(200).nullable().optional(),
  directorTitle: z.string().max(100).nullable().optional(),
});
export type UpdateClinicProfileRequest = z.infer<typeof UpdateClinicProfileRequest>;

/**
 * Готова ли карточка к печати документов.
 *
 * Полнота проверяется не схемой, а этой функцией, потому что она означает
 * разное для разных бумаг: плану лечения хватает наименования, адреса и
 * лицензии, договору нужен ещё и подписант. Экран печати спрашивает ровно то,
 * без чего документ недействителен, и называет недостающее по именам.
 */
export function missingForPrint(profile: ClinicProfile | null, kind: 'clinic' | 'lab' = 'clinic'): string[] {
  if (profile === null) return [kind === 'lab' ? 'реквизиты лаборатории' : 'реквизиты клиники'];

  const gaps: string[] = [];
  if (profile.inn === null) gaps.push('ИНН');
  if (profile.ogrn === null) gaps.push('ОГРН');
  if (profile.legalAddress === null) gaps.push('юридический адрес');
  // Лицензия Росздравнадзора — реквизит медицинского документа клиники;
  // реестру взаиморасчёта с лабораторией она не нужна.
  if (profile.licenseNumber === null && kind === 'clinic') gaps.push('номер лицензии');
  if (profile.directorName === null) gaps.push('руководитель, подписывающий документы');
  return gaps;
}
